import { DispatchApprovalVm } from './DispatchApprovalModel';

/**
 * Configuration for confirmation modal steps
 */
export interface ConfirmationStep {
  stepNumber: number;
  title: string;
  content: string;
  showDispatchDetails?: boolean;
  showStockImpact?: boolean;
  requiresReason?: boolean;
  buttonText: string;
  buttonType: 'primary' | 'danger' | 'default';
  isDestructive?: boolean;
}

/**
 * Configuration for the confirmation modal
 */
export interface ConfirmationModalConfig {
  modalTitle: string;
  modalWidth?: number;
  steps: ConfirmationStep[];
  dispatch?: DispatchApprovalVm;
  actionType: 'cancel' | 'delete' | 'disable' | 'remove' | 'custom';
  customData?: any;
  reasonRequired?: boolean;
  reasonMinLength?: number;
  reasonMaxLength?: number;
  reasonPlaceholder?: string;
}

/**
 * Result from confirmation modal
 */
export interface ConfirmationModalResult {
  confirmed: boolean;
  cancelled: boolean;
  reason?: string;
  stepCompleted: number;
  data?: any;
}

/**
 * Dispatch cancellation specific configuration
 */
export interface DispatchCancelConfig {
  dispatch: DispatchApprovalVm;
  willRestoreStock: boolean;
  contextMessage: string;
  stockWarningMessage?: string;
}

/**
 * Stock impact information for display
 */
export interface StockImpactInfo {
  willRestoreStock: boolean;
  impactMessage: string;
  affectedItems: StockImpactItem[];
}

/**
 * Individual stock impact item
 */
export interface StockImpactItem {
  packingNumber: string;
  quantity: number;
  unit: string;
}

/**
 * Validation result for reason input
 */
export interface ReasonValidation {
  isValid: boolean;
  errorMessage?: string;
  characterCount: number;
  remainingCharacters: number;
}

/**
 * Default configuration for dispatch cancellation
 */
export const DEFAULT_DISPATCH_CANCEL_CONFIG: Partial<ConfirmationModalConfig> = {
  modalTitle: 'Cancel Dispatch',
  modalWidth: 600,
  actionType: 'cancel',
  reasonRequired: true,
  reasonMinLength: 10,
  reasonMaxLength: 250,
  reasonPlaceholder: 'Please provide a detailed reason for cancelling this dispatch...'
};

/**
 * Utility class for confirmation modal operations
 */
export class ConfirmationModalUtils {
  
  /**
   * Create dispatch cancellation configuration
   */
  static createDispatchCancelConfig(
    dispatch: DispatchApprovalVm,
    willRestoreStock: boolean,
    contextMessage: string,
    stockWarningMessage?: string
  ): ConfirmationModalConfig {
    const steps: ConfirmationStep[] = [
      {
        stepNumber: 1,
        title: 'Confirm Cancellation',
        content: contextMessage,
        showDispatchDetails: true,
        buttonText: 'Continue',
        buttonType: 'primary'
      }
    ];

    // Add stock restoration warning step if needed
    if (willRestoreStock && stockWarningMessage) {
      steps.push({
        stepNumber: 2,
        title: 'Stock Restoration Warning',
        content: stockWarningMessage,
        showStockImpact: true,
        buttonText: 'I Understand, Continue',
        buttonType: 'danger',
        isDestructive: true
      });
    }

    // Add reason collection step
    steps.push({
      stepNumber: willRestoreStock ? 3 : 2,
      title: 'Cancellation Reason',
      content: 'Please provide a detailed reason for cancelling this dispatch.',
      requiresReason: true,
      buttonText: 'Cancel Dispatch',
      buttonType: 'danger',
      isDestructive: true
    });

    return {
      ...DEFAULT_DISPATCH_CANCEL_CONFIG,
      steps,
      dispatch
    } as ConfirmationModalConfig;
  }

  /**
   * Create generic destructive action configuration
   */
  static createDestructiveActionConfig(
    title: string,
    message: string,
    actionType: 'delete' | 'disable' | 'remove',
    requiresReason: boolean = false
  ): ConfirmationModalConfig {
    const steps: ConfirmationStep[] = [
      {
        stepNumber: 1,
        title: `Confirm ${title}`,
        content: message,
        buttonText: `Yes, ${title}`,
        buttonType: 'danger',
        isDestructive: true,
        requiresReason
      }
    ];

    return {
      modalTitle: title,
      modalWidth: 500,
      steps,
      actionType,
      reasonRequired: requiresReason,
      reasonMinLength: requiresReason ? 10 : 0,
      reasonMaxLength: 300,
      reasonPlaceholder: requiresReason ? `Please provide a reason for this ${actionType}...` : undefined
    };
  }

  /**
   * Validate reason input
   */
  static validateReason(
    reason: string,
    minLength: number,
    maxLength: number
  ): ReasonValidation {
    const trimmedReason = reason?.trim() || '';
    const characterCount = trimmedReason.length;
    const remainingCharacters = maxLength - characterCount;

    if (characterCount < minLength) {
      return {
        isValid: false,
        errorMessage: `Reason must be at least ${minLength} characters long`,
        characterCount,
        remainingCharacters
      };
    }

    if (characterCount > maxLength) {
      return {
        isValid: false,
        errorMessage: `Reason cannot exceed ${maxLength} characters`,
        characterCount,
        remainingCharacters
      };
    }

    return {
      isValid: true,
      characterCount,
      remainingCharacters
    };
  }

  /**
   * Extract stock impact information from dispatch
   */
  static extractStockImpact(dispatch: DispatchApprovalVm): StockImpactInfo {
    return {
      willRestoreStock: dispatch.IsStockRemoved === true,
      impactMessage: dispatch.IsStockRemoved 
        ? 'Stock will be restored to inventory upon cancellation'
        : 'No stock restoration required',
      affectedItems: [
        {
          packingNumber: dispatch.PackingNumber,
          quantity: dispatch.DispatchQuantity || 0,
          unit: 'Mtrs'
        }
      ]
    };
  }
}
